import React, { useEffect, useState } from 'react'
import { IContextMenuState, IFile } from 'types'
import { INITIAL_CONTEXT_MENU_STATE } from 'app-constants'
import api, { apiPrefix } from 'helpers/api'
import { getAuthToken } from 'helpers/auth'
import Dialog from 'components/dialog'
import ActionDialog from 'components/dialog/action'
import Button from 'components/button'
import Spinner from 'components/spinner'
import Tooltip from 'components/tooltip'
import Menu from 'components/menu'
import { MenuItem, MenuList, MenuSeparator } from 'components/menu/items'
import { ArrowUpRight, AttachedFile, Delete, Dots, DownloadTray, Plus, Triangle } from 'components/icons'

// In the manner of shadcn/ui's dialog: one file at a time, named in the title with a ⋯ beside it for what can
// be done to it (download, a PDF's or image's full view, add, remove), and its type and size under it, with
// links to download it and, for a PDF or image, to open it in full in a new tab. With more than one file, arrows by the close button (or the arrow keys) step
// through them. There's no foot, so the file, on the sunken fill, has the height: a PDF, image, video, sound or
// text file is played or shown, anything else offers a Download button.

/** A file's size in bytes, as people read it: 640 B, 12 KB, 3.4 MB. */
export const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

type PreviewKind = 'image' | 'video' | 'audio' | 'pdf' | 'text' | null

const CSV = /^(text\/csv|application\/csv|application\/vnd\.ms-excel)$/i

/** How a file of this type is shown, or null for one that can only be downloaded. */
export const previewKind = (mimetype: string): PreviewKind => {
  if (/^image\//.test(mimetype)) return 'image'
  if (/^video\//.test(mimetype)) return 'video'
  if (/^audio\//.test(mimetype)) return 'audio'
  if (mimetype === 'application/pdf') return 'pdf'
  // HTML would run as a page, so it isn't shown.
  if ((/^text\//.test(mimetype) || CSV.test(mimetype)) && mimetype !== 'text/html') return 'text'
  return null
}

/** A blob's text, or '' if it can't be read. */
const readText = (blob: Blob): Promise<string> =>
  new Promise((resolve) => {
    const reader = new FileReader()
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '')
    reader.onerror = () => resolve('')
    reader.readAsText(blob)
  })

/** What a failed request says went wrong: the API's message, or the text it sent. */
const errorMessage = (text: string): string | undefined => {
  try {
    return JSON.parse(text)?.detail?.message ?? (text || undefined)
  } catch {
    return text || undefined
  }
}

type Source =
  | { status: 'loading' }
  | { status: 'ready'; src: string; blob: Blob; text?: string }
  | { status: 'error'; message?: string }

/**
 * The file's contents, fetched with the user's token and held as a local link for as long as it's shown. Files
 * kept on a table before signed links (version 1) are fetched from the table, by their column and name.
 */
const useFileSource = (file: IFile | undefined, tableId?: string, columnId?: string): Source => {
  const [source, setSource] = useState<Source>({ status: 'loading' })

  useEffect(() => {
    if (!file) return
    let src: string | undefined
    let cancelled = false
    const request = new XMLHttpRequest()

    const load = async () => {
      const token = getAuthToken()
      if (!token) return setSource({ status: 'error' })
      let url: string
      if (file.version === 1) {
        // The column and the file's name as the route's query (columnId, filename).
        const query = new URLSearchParams({ columnId: columnId ?? '', filename: file.filename })
        url = `${apiPrefix}/table/${tableId}/file?${query}`
      } else {
        const signed = await api.signUrl(file.url).then(
          (response) => response.data.url,
          (error: Error) => {
            if (!cancelled) setSource({ status: 'error', message: error?.message })
            return null
          }
        )
        if (!signed) return
        url = signed
      }
      if (cancelled) return
      request.open('GET', url)
      request.responseType = 'blob'
      // Only the table's own route needs the reader's sign-in; a signed link carries its own token, so the
      // sign-in isn't sent with it.
      if (file.version === 1) request.setRequestHeader('Authorization', token)
      request.onload = async () => {
        if (cancelled) return
        if (request.status === 200) {
          // A CSV is shown as the text it is, rather than offered as a download by the frame.
          const blob = new Blob([request.response], { type: CSV.test(file.mimetype) ? 'text/plain' : file.mimetype })
          src = URL.createObjectURL(blob)
          // Text is shown on a page of our own, in our type, rather than in a frame.
          const text = previewKind(file.mimetype) === 'text' ? await readText(blob) : undefined
          if (!cancelled) setSource({ status: 'ready', src, blob, text })
        } else {
          setSource({ status: 'error', message: errorMessage(await readText(request.response as Blob)) })
        }
      }
      request.onerror = () => !cancelled && setSource({ status: 'error' })
      request.send()
    }

    setSource({ status: 'loading' })
    load()
    return () => {
      cancelled = true
      request.abort()
      if (src) URL.revokeObjectURL(src)
    }
  }, [file?.publicId, file?.url, file?.version, tableId, columnId])

  return source
}

interface PreviewProps {
  file: IFile
  source: Source
  onDownload: () => void
}

/** The file as its type allows, or why it can't be. */
const Preview = ({ file, source, onDownload }: PreviewProps): React.JSX.Element => {
  if (source.status === 'loading') return <Spinner className="size-6" />
  if (source.status === 'error')
    return (
      <p className="m-0 max-w-sm px-6 text-center text-pretty text-muted">
        {source.message || "This file couldn't be loaded. Close it and open it again."}
      </p>
    )

  /* eslint-disable jsx-a11y/media-has-caption */
  switch (previewKind(file.mimetype)) {
    case 'image':
      return <img className="size-full object-contain" src={source.src} alt={file.filename} />
    case 'video':
      return <video className="size-full object-contain" src={source.src} controls />
    case 'audio':
      return <audio className="w-full max-w-md" src={source.src} controls />
    case 'pdf':
      // The browser's own PDF reader, without its toolbar and page list.
      return (
        <object
          className="size-full"
          data={`${source.src}#toolbar=0&navpanes=0`}
          type={file.mimetype}
          aria-label={`Preview of ${file.filename}`}
        >
          <p className="m-0 p-6 text-center text-muted">This PDF can&apos;t be shown here. Download it to open it.</p>
        </object>
      )
    case 'text':
      return (
        <pre className="m-0 size-full overflow-auto bg-field p-5 font-mono text-[12px] leading-5 break-words whitespace-pre-wrap text-body">
          {source.text}
        </pre>
      )
    default:
      return (
        <div className="flex flex-col items-center gap-2.5 px-6 text-center text-muted">
          <span className="flex size-10 items-center justify-center rounded-lg border border-divider bg-surface text-body shadow-xs">
            <AttachedFile />
          </span>
          <span>There&apos;s no preview for this type of file.</span>
          <Button internalType="outline" className="mt-1 gap-1.5" onClick={onDownload}>
            <DownloadTray />
            Download
          </Button>
        </div>
      )
  }
  /* eslint-enable jsx-a11y/media-has-caption */
}

interface FileViewerProps {
  files: IFile[]
  open: boolean
  onOpenChange: (open: boolean) => void
  /** The file to show first. */
  initialIndex?: number
  /** For files kept on a table before signed links: the table and column they're in. */
  tableId?: string
  columnId?: string
  /** Removes the file at this index from where it's kept. Without it, there's no Remove file. */
  onRemove?: (index: number) => void
  /** Chooses more files to add. Without it, there's no Add files. */
  onAdd?: () => void
}

const MENU_WIDTH = 180

// A link on the line under the name, in its grey, underlined lightly, darkening on hover.
const LINE_LINK = [
  'h-auto shrink-0 gap-0.5 p-0 font-normal text-muted underline decoration-faint underline-offset-2',
  'hover:bg-transparent hover:text-body hover:decoration-current [&_svg]:size-3.5'
].join(' ')

/** Views files one at a time, in a dialog. */
const FileViewer: React.FC<FileViewerProps> = ({
  files,
  open,
  onOpenChange,
  initialIndex = 0,
  tableId,
  columnId,
  onRemove,
  onAdd
}) => {
  const [index, setIndex] = useState<number>(initialIndex)
  const [menu, setMenu] = useState<IContextMenuState>(INITIAL_CONTEXT_MENU_STATE)
  const [confirmRemove, setConfirmRemove] = useState<boolean>(false)

  useEffect(() => {
    if (open) setIndex(initialIndex)
    setConfirmRemove(false)
  }, [open, initialIndex])

  // Once the last file is removed there's nothing to show.
  useEffect(() => {
    if (open && files.length === 0) onOpenChange(false)
  }, [open, files.length])

  const at = Math.min(index, Math.max(files.length - 1, 0))
  const file: IFile | undefined = files[at]
  const source = useFileSource(open ? file : undefined, tableId, columnId)

  const step = (by: number) => setIndex(Math.min(Math.max(at + by, 0), files.length - 1))

  useEffect(() => {
    if (!open || files.length < 2 || confirmRemove) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') step(-1)
      if (event.key === 'ArrowRight') step(1)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  })

  if (!file) return null

  const download = () => {
    if (source.status !== 'ready') return
    const link = document.createElement('a')
    link.href = source.src
    link.download = file.filename
    link.click()
  }

  // A PDF or image in a tab of its own (a PDF in the browser's own reader, toolbar and all), for a close look.
  // It has a link of its own, kept for the life of the page, so it can still be saved or printed after this
  // closes.
  const hasFullView = ['pdf', 'image'].includes(previewKind(file.mimetype) ?? '')
  const openFullView = () => {
    if (source.status !== 'ready') return
    window.open(URL.createObjectURL(source.blob), '_blank', 'noopener')
  }

  const closeMenu = () => setMenu(INITIAL_CONTEXT_MENU_STATE)
  const fromMenu = (action: () => void) => () => {
    closeMenu()
    action()
  }
  const ready = source.status === 'ready'

  // What can be done to the file, beside its name: download it, open a PDF or image in full, add more, and
  // remove it, in red, after asking.
  const actions = (
    <>
      <Button
        internalType="transparent"
        className="size-6 shrink-0 p-0 text-muted hover:text-body [&_svg]:size-3.5"
        aria-label="File actions"
        aria-haspopup="menu"
        aria-expanded={menu.open}
        onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
          if (menu.open) return closeMenu()
          const box = event.currentTarget.getBoundingClientRect()
          setMenu({ open: true, top: `${box.bottom + 4}px`, left: `${box.left}px`, right: 'auto', bottom: 'auto' })
        }}
      >
        <Dots />
      </Button>
      <Menu id="file-viewer-menu" menuState={menu} setMenuState={setMenu} width={MENU_WIDTH}>
        <MenuList>
          <MenuItem icon={<DownloadTray />} disabled={!ready} onClick={fromMenu(download)}>
            Download
          </MenuItem>
          {hasFullView && (
            <MenuItem icon={<ArrowUpRight />} disabled={!ready} onClick={fromMenu(openFullView)}>
              Full screen preview
            </MenuItem>
          )}
          {onAdd && (
            <MenuItem icon={<Plus />} onClick={fromMenu(onAdd)}>
              Add files
            </MenuItem>
          )}
          {onRemove && (
            <>
              <MenuSeparator />
              <MenuItem icon={<Delete />} variant="destructive" onClick={fromMenu(() => setConfirmRemove(true))}>
                Remove file
              </MenuItem>
            </>
          )}
        </MenuList>
      </Menu>
    </>
  )

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={onOpenChange}
        width="viewer"
        title={
          // A long name is cut short, and shown in full on hover.
          <Tooltip content={file.filename} showOnlyIfTruncated className="max-w-[min(560px,calc(100vw-16px))]">
            <span className="block truncate">{file.filename}</span>
          </Tooltip>
        }
        titleAction={actions}
        descriptionEnd={
          // Links in the line's own grey, each after its own ·, so they read as part of the line: the download
          // a tap away rather than in the ⋯, and a PDF's or image's full view.
          <>
            <span aria-hidden className="text-muted">
              ·
            </span>
            <Button internalType="transparent" className={LINE_LINK} disabled={!ready} onClick={download}>
              Download
            </Button>
            {hasFullView && (
              <>
                <span aria-hidden className="text-muted">
                  ·
                </span>
                <Button internalType="transparent" className={LINE_LINK} disabled={!ready} onClick={openFullView}>
                  Full screen preview
                  <ArrowUpRight />
                </Button>
              </>
            )}
          </>
        }
        description={[file.extension?.toUpperCase(), formatFileSize(file.filesize)].filter(Boolean).join(' · ')}
        headerEnd={
          // With more than one file: the one shown, between the files either side.
          files.length > 1 && (
            <div className="flex items-center">
              <Button
                internalType="transparent"
                className="size-7 p-0"
                aria-label="Previous file"
                disabled={at === 0}
                onClick={() => step(-1)}
              >
                <Triangle type="left" />
              </Button>
              <span className="px-0.5 text-muted tabular-nums">
                {at + 1} of {files.length}
              </span>
              <Button
                internalType="transparent"
                className="size-7 p-0"
                aria-label="Next file"
                disabled={at === files.length - 1}
                onClick={() => step(1)}
              >
                <Triangle type="right" />
              </Button>
            </div>
          )
        }
      >
        {/* The stage: the file on the sunken fill. */}
        <div className="flex h-[min(720px,calc(100vh-132px))] items-center justify-center overflow-hidden rounded-lg border border-divider bg-surface-sunken">
          <Preview file={file} source={source} onDownload={download} />
        </div>
      </Dialog>
      {confirmRemove && (
        <ActionDialog
          title="Remove file"
          description={`\u2018${file.filename}\u2019 will be removed from this field.`}
          confirmLabel="Remove"
          danger
          onClose={() => setConfirmRemove(false)}
          onConfirm={async () => {
            setConfirmRemove(false)
            onRemove?.(at)
          }}
        />
      )}
    </>
  )
}

export default FileViewer
